import { memo } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Heart, Users, ArrowRight, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Web3CampaignBanner = memo(function Web3CampaignBanner() {
  const { t } = useTranslation();

  return (
    <section className="py-16 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-y border-primary/10">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          
          <div className="flex-1 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <ShieldCheck className="w-4 h-4" />
              <span>Web3 Verified Connections</span>
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">
              Personal Ads are Back. <span className="text-primary">Safe & Verified.</span>
            </h2>
            
            <p className="text-lg text-muted-foreground leading-relaxed">
              We're bringing back personal ads without the spam and scams. 
              By using Web3 wallet verification, we ensure every "Romance" or "Friendship" 
              post is from a real human with a staked reputation.
            </p>

            <ul className="space-y-3">
              {[
                { icon: Users, text: "Sybil-resistant identity verification" },
                { icon: Heart, text: "Curated community of real people" },
                { icon: ShieldCheck, text: "Zero-knowledge privacy protection" },
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-background flex items-center justify-center border shadow-sm text-primary">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <span className="font-medium">{item.text}</span>
                </li>
              ))}
            </ul>

            <div className="pt-4 flex flex-wrap gap-4">
              <Link href="/browse?category=romance">
                <Button size="lg" className="gap-2">
                  Explore Connections <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link href="/post">
                <Button variant="outline" size="lg">
                  Create Verified Profile
                </Button>
              </Link>
            </div>
          </div>

          <div className="flex-1 w-full max-w-md lg:max-w-none relative">
            <div className="aspect-square rounded-full bg-gradient-to-tr from-rose-500/20 via-primary/20 to-blue-500/20 absolute blur-3xl inset-0" />
            
            <div className="relative grid grid-cols-2 gap-4">
               {/* Decorative cards illustrating the concept */}
               <div className="space-y-4 pt-8">
                  <div className="bg-card p-4 rounded-xl shadow-lg border border-border/50 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-500">
                        <Heart className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="h-2 w-20 bg-muted rounded mb-1" />
                        <div className="h-2 w-12 bg-muted rounded" />
                      </div>
                    </div>
                    <div className="space-y-2">
                         <div className="h-2 w-full bg-muted/50 rounded" />
                         <div className="h-2 w-3/4 bg-muted/50 rounded" />
                    </div>
                    <div className="mt-4 flex justify-between items-center text-xs text-muted-foreground">
                       <span>Verified</span>
                       <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    </div>
                  </div>

                  <div className="bg-card p-4 rounded-xl shadow-lg border border-border/50 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500">
                        <Users className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="h-2 w-20 bg-muted rounded mb-1" />
                        <div className="h-2 w-12 bg-muted rounded" />
                      </div>
                    </div>
                    <div className="space-y-2">
                         <div className="h-2 w-full bg-muted/50 rounded" />
                         <div className="h-2 w-3/4 bg-muted/50 rounded" />
                    </div>
                     <div className="mt-4 flex justify-between items-center text-xs text-muted-foreground">
                       <span>Verified</span>
                       <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    </div>
                  </div>
               </div>

               <div className="space-y-4">
                  <div className="bg-card p-4 rounded-xl shadow-lg border border-border/50 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500">
                        <Activity className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="h-2 w-20 bg-muted rounded mb-1" />
                        <div className="h-2 w-12 bg-muted rounded" />
                      </div>
                    </div>
                    <div className="space-y-2">
                         <div className="h-2 w-full bg-muted/50 rounded" />
                         <div className="h-2 w-3/4 bg-muted/50 rounded" />
                    </div>
                     <div className="mt-4 flex justify-between items-center text-xs text-muted-foreground">
                       <span>Verified</span>
                       <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    </div>
                  </div>
               </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
});

Web3CampaignBanner.displayName = "Web3CampaignBanner";
